import React from "react";
import styled from "styled-components";
import { Empty } from "antd";
import {
  Card,
  ElementContainer,
  FlexibleContainer,
  RowContainer,
} from "../../containers/styledComponents";
import { VotingAlignmentBars } from "./VotingAlignmentBars";
import { CompassParagraph } from "../styledComponents";
import {
  LegendCircle,
  StyledPlaceholder,
} from "../../visuals/styledComponents";
import { votingAlignmentBarsPalette } from "../../../styles";
import { ToggleAccordionBlueText } from "../../buttons/ToggleAccordionBlueText";
import {
  StewardshipFilterParam,
  VotingAlignment as VotingAlignmentType,
} from "../../../models/stewardshipModels";
import { VotingRecordModal } from "./VotingRecordModal";
import { CompanySummary } from "../../../models/CompanyprofileModels";
import { CustomDropdown } from "../../visuals/PageFilter/PageFilter";
import { ExportButton } from "../../buttons/ExportButton";
import { CSVExportConfig } from "../../../services/FileExport";
import { VotingSummaryTable } from "../../../pages/StewardshipHomepage/VotingSummaryTable";
import { useVotingAlignment } from "../../../hooks/useVotingAlignment";

const LegendContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
  margin-top: 8px;
  padding-top: 8px;
`;

const LegendItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  white-space: nowrap;
`;

const LegendLabel = styled.span`
  color: #8b909a;
  font-size: 12px;
  line-height: 16px;
`;

const AlignmentHeader = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
`;

const AlignmentControls = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const ManagerFilter = styled.div`
  margin-top: 8px;
`;

const votingAlignmentLegend: {
  id: "aligned" | "misaligned";
  displayValue: string;
}[] = [
  {
    id: "aligned",
    displayValue: "Aligned Votes",
  },
  {
    id: "misaligned",
    displayValue: "Misaligned Votes",
  },
];

const VOTING_ALIGNMENT_SUMMARY_EXPORT_CONFIG: CSVExportConfig<VotingAlignmentType> =
  {
    filename: "voting_alignment_summary.csv",
    columns: [
      { key: "manager", label: "Manager" },
      { key: "total_votes_count", label: "Mapped/Total Votes Count" },
      { key: "aligned_votes_count", label: "Aligned Votes Count" },
      { key: "alignedPct", label: "Aligned Votes Percentage" },
    ],
  };

export const VotingAlignment: React.FC<{
  filters: StewardshipFilterParam;
  companySummaryData: CompanySummary | null;
}> = ({ filters, companySummaryData }) => {
  const {
    // Data
    votingAlignmentData,
    dataSource,
    votingCompanySummaryRecords,
    rawVotingData,

    // State
    isCardExpanded,
    shouldDisplayVotingAlignment,
    shouldDisplayVotingRecordModal,
    modalVotingRecordId,
    votingSummaryPagination,

    // Loading States
    isLoading,
    isCompanyVotingSummaryLoading,

    // Table Configuration
    totalRecords,

    // Options
    managerOptions,
    placeholderOption,

    // Sorter
    sorter,

    // Actions
    handleHideVotingRecordModal,
    handleCardExpand,
    handleTableChange,
    setVotingAlignmentReference,
    votingCompanySummaryColumns,

    // Refs
    scrollTargetRef,
  } = useVotingAlignment(filters);

  return (
    <RowContainer ml={10} mt={1} ref={scrollTargetRef}>
      <Card w={100} h={"auto"} m="0 30px 0 10px" isLoading={isLoading}>
        <ElementContainer flexDirection="column">
          <AlignmentHeader>
            <AlignmentControls>
              <CompassParagraph fontSize={16} bold={true}>
                Voting Alignment
              </CompassParagraph>
              <ManagerFilter>
                <CustomDropdown
                  options={managerOptions}
                  placeholder={placeholderOption}
                  onSelectOption={setVotingAlignmentReference}
                  small={true}
                  isLoading={isLoading}
                />
              </ManagerFilter>
            </AlignmentControls>
          </AlignmentHeader>
          {shouldDisplayVotingAlignment && (
            <>
              <VotingAlignmentBars data={votingAlignmentData} />
              <LegendContainer>
                {votingAlignmentLegend.map((item, index) => (
                  <LegendItem key={item.id}>
                    <LegendCircle color={votingAlignmentBarsPalette[index]} />
                    <LegendLabel>{item.displayValue}</LegendLabel>
                  </LegendItem>
                ))}
              </LegendContainer>
            </>
          )}
          {shouldDisplayVotingAlignment && (
            <RowContainer mb={-1}>
              <ToggleAccordionBlueText
                handleChange={handleCardExpand}
                isOpen={isCardExpanded}
              />
            </RowContainer>
          )}
          <RowContainer mb={-1}>
            {isCardExpanded &&
              !isLoading &&
              <VotingSummaryTable
                isLoading={isCompanyVotingSummaryLoading}
                data={votingCompanySummaryRecords}
                columns={votingCompanySummaryColumns}
                pagination={votingSummaryPagination}
                sorter={sorter}
                totalRecords={totalRecords}
                handleTableChange={handleTableChange}
              />}
          </RowContainer>
          {EmptyContentPlaceholders(shouldDisplayVotingAlignment, isLoading)}
        </ElementContainer>
        {!isLoading && !isCardExpanded && (
          <ExportButton
            data={votingAlignmentData || []}
            {...VOTING_ALIGNMENT_SUMMARY_EXPORT_CONFIG}
          />
        )}
      </Card>
      <VotingRecordModal
        shouldDisplay={shouldDisplayVotingRecordModal}
        onHide={handleHideVotingRecordModal}
        modalRecordId={modalVotingRecordId}
        data={rawVotingData}
        votingTableData={dataSource}
        companySummaryData={companySummaryData}
      />
    </RowContainer>
  );
};

const EmptyContentPlaceholders = (
  shouldDisplayVotingAlignment: boolean,
  isLoading: boolean,
): JSX.Element | null => {
  if (!shouldDisplayVotingAlignment) {
    return isLoading ? (
      <FlexibleContainer fg={1} as="stretch" fd="column">
        <StyledPlaceholder xs={10} size="lg" bg="white" />
        <StyledPlaceholder xs={10} size="lg" bg="white" />
        <StyledPlaceholder xs={10} size="lg" bg="white" />
      </FlexibleContainer>
    ) : (
      <RowContainer jc="center">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={"No voting records"}
        />
      </RowContainer>
    );
  }
  return null;
};
